import React, { useState } from 'react';
import apiService from '../../services/ApiService';
import { usePortfolioContext } from './PortfolioContext';

const TAG_COLORS = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
    '#ec4899', '#06b6d4', '#f97316', '#84cc16', '#6366f1',
];

export default function TagManager() {
    const { selectedGroup, selectGroup, canWrite } = usePortfolioContext();
    const [showCreate, setShowCreate] = useState(false);
    const [newTagName, setNewTagName] = useState('');
    const [newTagColor, setNewTagColor] = useState(TAG_COLORS[0]);
    const [saving, setSaving] = useState(false);
    const [assigningAssetId, setAssigningAssetId] = useState<number | null>(null);

    if (!selectedGroup) return null;

    const tags = selectedGroup.tags || [];

    const handleCreateTag = async () => {
        if (!newTagName.trim()) return;
        setSaving(true);
        try {
            await apiService.createPortfolioTag(selectedGroup.id, {
                name: newTagName.trim(),
                color: newTagColor,
            });
            setNewTagName('');
            setShowCreate(false);
            await selectGroup(selectedGroup.id);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteTag = async (tagId: number) => {
        await apiService.deletePortfolioTag(selectedGroup.id, tagId);
        await selectGroup(selectedGroup.id);
    };

    const handleAssignTag = async (assetId: number, tagIds: number[]) => {
        await apiService.updatePortfolioAsset(selectedGroup.id, assetId, { tag_ids: tagIds });
        await selectGroup(selectedGroup.id);
        setAssigningAssetId(null);
    };

    return (
        <div className="space-y-6">
            {/* Tag list */}
            <section className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-semibold text-slate-900">Tags ({tags.length})</h3>
                    {canWrite && (
                        <button
                            onClick={() => setShowCreate(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                        >
                            <span className="material-symbols-outlined text-[16px]">add</span>
                            New Tag
                        </button>
                    )}
                </div>

                {tags.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">No tags yet. Create tags to categorize your assets.</p>
                ) : (
                    <div className="flex flex-wrap gap-2">
                        {tags.map((tag: any) => (
                            <div
                                key={tag.id}
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-white"
                            >
                                <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: tag.color || '#94a3b8' }} />
                                <span className="text-sm font-medium text-slate-700">{tag.name}</span>
                                {canWrite && (
                                    <button
                                        onClick={() => handleDeleteTag(tag.id)}
                                        className="p-0.5 rounded-full hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                                        title="Delete tag"
                                    >
                                        <span className="material-symbols-outlined text-[14px]">close</span>
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Create tag inline form */}
                {showCreate && (
                    <div className="mt-4 p-4 border border-slate-200 rounded-lg bg-slate-50">
                        <div className="flex items-end gap-3">
                            <div className="flex-1">
                                <label className="block text-xs font-medium text-slate-600 mb-1">Tag Name</label>
                                <input
                                    type="text"
                                    value={newTagName}
                                    onChange={(e) => setNewTagName(e.target.value)}
                                    className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., Solar, France, 2024"
                                    autoFocus
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleCreateTag(); }}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-slate-600 mb-1">Color</label>
                                <div className="flex gap-1">
                                    {TAG_COLORS.map((color) => (
                                        <button
                                            key={color}
                                            onClick={() => setNewTagColor(color)}
                                            className={`w-6 h-6 rounded-full border-2 transition-all ${
                                                newTagColor === color ? 'border-slate-900 scale-110' : 'border-transparent'
                                            }`}
                                            style={{ backgroundColor: color }}
                                        />
                                    ))}
                                </div>
                            </div>
                            <button
                                onClick={handleCreateTag}
                                disabled={!newTagName.trim() || saving}
                                className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                            >
                                {saving ? '...' : 'Add'}
                            </button>
                            <button
                                onClick={() => { setShowCreate(false); setNewTagName(''); }}
                                className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </section>

            {/* Assign tags to assets */}
            {tags.length > 0 && selectedGroup.assets.length > 0 && (
                <section className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="text-base font-semibold text-slate-900 mb-4">Assign Tags to Assets</h3>
                    <div className="space-y-3">
                        {selectedGroup.assets.map((asset: any) => (
                            <div key={asset.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-b-0">
                                <div>
                                    <span className="text-sm font-medium text-slate-900">{asset.model_name}</span>
                                    <span className="text-sm text-slate-500 ml-2">{asset.company_name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    {/* Current tags */}
                                    {asset.tags?.map((t: any) => (
                                        <span
                                            key={t.id}
                                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                                            style={{ backgroundColor: (t.color || '#94a3b8') + '20', color: t.color || '#64748b' }}
                                        >
                                            {t.name}
                                        </span>
                                    ))}
                                    {/* Edit tags button */}
                                    {canWrite && (assigningAssetId === asset.id ? (
                                        <TagAssigner
                                            tags={tags}
                                            currentTagIds={(asset.tags || []).map((t: any) => t.id)}
                                            onSave={(tagIds) => handleAssignTag(asset.id, tagIds)}
                                            onCancel={() => setAssigningAssetId(null)}
                                        />
                                    ) : (
                                        <button
                                            onClick={() => setAssigningAssetId(asset.id)}
                                            className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                                            title="Edit tags"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">sell</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}

function TagAssigner({ tags, currentTagIds, onSave, onCancel }: {
    tags: any[];
    currentTagIds: number[];
    onSave: (tagIds: number[]) => void;
    onCancel: () => void;
}) {
    const [selected, setSelected] = useState<Set<number>>(new Set(currentTagIds));

    const toggle = (id: number) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelected(next);
    };

    return (
        <div className="flex items-center gap-1">
            {tags.map((tag: any) => (
                <button
                    key={tag.id}
                    onClick={() => toggle(tag.id)}
                    className={`px-2 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                        selected.has(tag.id)
                            ? 'border-transparent text-white'
                            : 'border-slate-200 text-slate-500 hover:border-slate-400'
                    }`}
                    style={selected.has(tag.id) ? { backgroundColor: tag.color || '#64748b' } : {}}
                >
                    {tag.name}
                </button>
            ))}
            <button onClick={() => onSave(Array.from(selected))} className="p-1 rounded hover:bg-green-50 text-green-600" title="Save">
                <span className="material-symbols-outlined text-[16px]">check</span>
            </button>
            <button onClick={onCancel} className="p-1 rounded hover:bg-slate-100 text-slate-400" title="Cancel">
                <span className="material-symbols-outlined text-[16px]">close</span>
            </button>
        </div>
    );
}
